import { useCallback, useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import { Edit as EditIcon } from "@mui/icons-material";
import { Button, Chip, IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import { apiClient, statusColors, statusLabels, getErrorMessage } from "@/features/shared";
import { SearchTextField, useSnackbar } from "@/features/ui";
import { useSearchFilter, useServerPagination } from "@/hooks";

export const Companies = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);

  const { setRowCount, paginationParams, dataGridPaginationProps, resetParams } =
    useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiClient.get("companies", {
        params: {
          ...paginationParams,
          ...(keyword && { name: keyword }),
          withEntityTaxInformation: true,
        },
      });
      setCompanies(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat perusahaan"));
    } finally {
      setLoading(false);
    }
  }, [paginationParams, keyword, setRowCount, showSnackbar]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const toolbarContent = hasPermission(PermissionEnum.CREATE_COMPANIES) && (
    <Button variant="contained" onClick={() => navigate("create")}>
      Tambah
    </Button>
  );

  return (
    <PageContainer
      maxWidth="xl"
      breadcrumbs={[]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={3}>
        <Grid container sx={{ justifyContent: "flex-end" }}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari nama perusahaan"
              {...searchProps}
            />
          </Grid>
        </Grid>
        <DataGrid
          rows={companies}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "name",
              headerName: "Nama",
              flex: 3,
              sortable: false,
            },
            {
              field: "entityTaxInformation",
              headerName: "NPWP",
              flex: 2,
              sortable: false,
              valueGetter: (value) => value?.taxIdentificationNumber || "-",
            },
            {
              field: "status",
              headerName: "Status",
              flex: 1,
              sortable: false,
              renderCell: ({ row }) => (
                <Chip
                  label={statusLabels[row.status]}
                  color={statusColors[row.status]}
                />
              ),
            },
            ...(hasPermission(PermissionEnum.UPDATE_COMPANIES)
              ? [
                  {
                    field: "actions",
                    type: "actions",
                    headerName: "Aksi",
                    getActions: (params) => [
                      <IconButton
                        key="edit"
                        onClick={() => navigate(`${params.id}/edit`)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>,
                    ],
                  },
                ]
              : []),
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
      </Stack>
    </PageContainer>
  );
};
